// @ts-nocheck

import { useEffect, useState } from "react";
import ReactECharts from "echarts-for-react";
import axios from "axios";
import useStoreTimToa from "../../store/useStoreTimToa";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Icons } from "../../components/spinner";
import { SelectCenterCost } from "../../components/select-react-center-cost";
import { SelectBranch } from "../../components/select-react-branches";
import { DatePickerStart } from "../../components/select-date-start";
import { DatePickerEnd } from "../../components/select-date-end";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { formattUrlParams } from "../../utils/formattUrlParams";
import { Slider } from "../../components/ui/slider";

export function ConsumoKmPorLitro() {
  const [options, setOptions] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [maxKmL, setMaxKmL] = useState(100);
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [kmFilter, setKmFilter] = useState(10); // Initialize with 10 instead of 0
  const [supplyFilter, setSupplyFilter] = useState(0);
  const [filterValues, setFilterValues] = useState({
    kmL: 10, // Initialize with 10 instead of 0
    supplies: 0,
  });
  const { centerCost, branch, startDate, endDate } = useStoreTimToa((state) => state);

  const formatCurrency = (value) => {
    if (typeof value === "string") {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(parseFloat(value.replace(".", "").replace(",", ".")));
    } else {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(value);
    }
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat("pt-BR").format(value);
  };

  const calculateMaxKmL = (data) => {
    if (!data?.abastecimentos_por_motorista?.length) return 100;

    // Filter out extreme outliers (values over 200)
    const kmValues = data.abastecimentos_por_motorista
      .map((d) => parseFloat(d.quilometrosporlitro))
      .filter((val) => !isNaN(val) && val < 200);

    if (kmValues.length === 0) return 100;
    return Math.ceil(Math.max(...kmValues));
  };

  const updateOptions = () => {
    if (!data || !data.abastecimentos_por_motorista) {
      console.error("Data is missing or invalid", data);
      return;
    }

    try {
      // Make sure to filter out any records with invalid data
      const validDrivers = data.abastecimentos_por_motorista.filter((driver) => {
        const kmPerLiter = parseFloat(driver.quilometrosporlitro);
        return !isNaN(kmPerLiter) && kmPerLiter < 200; // Filter extreme outliers
      });

      const drivers = validDrivers.filter((driver) => {
        const kmPerLiter = parseFloat(driver.quilometrosporlitro);
        const supplies = parseInt(driver.abastecimentos_realizados);

        return kmPerLiter <= filterValues.kmL && supplies >= filterValues.supplies;
      });

      if (drivers.length === 0) {
        console.warn("No drivers match the filter criteria");
        setFilteredDrivers([]);
        setOptions({});
        return;
      }

      setFilteredDrivers(drivers);

      // Sort drivers by KM/L for better visualization
      drivers.sort(
        (a, b) => parseFloat(b.quilometrosporlitro) - parseFloat(a.quilometrosporlitro)
      );

      const driverNames = drivers.map((d) => d.nome_motorista);

      const option = {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          formatter: function (params) {
            let tooltip = `<div style="font-weight:bold">${params[0].name}</div>`;

            params.forEach((param) => {
              let value = param.value;
              if (param.seriesName === "Valor (R$)") {
                value = formatCurrency(value);
              } else {
                value = formatNumber(value);
              }

              tooltip += `<div>${param.seriesName}: ${value}</div>`;
            });

            return tooltip;
          },
        },
        legend: {
          textStyle: {
            color: "#ffffff",
          },
        },
        grid: {
          left: "5%",
          right: "4%",
          bottom: "3%",
          containLabel: true,
        },
        dataZoom: [
          {
            type: "inside",
            yAxisIndex: 0,
            filterMode: "empty",
          },
          {
            type: "slider",
            yAxisIndex: 0,
            right: 0,
          },
        ],
        xAxis: {
          type: "value",
          axisLabel: {
            color: "#ffffff",
          },
        },
        yAxis: {
          type: "category",
          data: driverNames,
          axisLabel: {
            color: "#ffffff",
            width: 100,
            overflow: "truncate",
            interval: 0,
          },
        },
        series: [
          {
            name: "Abastecimentos",
            type: "bar",
            stack: "total",
            label: {
              show: true,
              position: "inside",
              color: "#ffffff",
            },
            emphasis: {
              focus: "series",
            },
            data: drivers.map((d) => parseInt(d.abastecimentos_realizados)),
          },
          {
            name: "Valor (R$)",
            type: "bar",
            stack: "total",
            label: {
              show: true,
              position: "inside",
              color: "#ffffff",
            },
            emphasis: {
              focus: "series",
            },
            data: drivers.map((d) => {
              const valor = d.valor;
              if (typeof valor === "string") {
                return parseFloat(valor.replace(".", "").replace(",", "."));
              }
              return valor;
            }),
          },
          {
            name: "Litros",
            type: "bar",
            stack: "total",
            label: {
              show: true,
              position: "inside",
              color: "#ffffff",
            },
            emphasis: {
              focus: "series",
            },
            data: drivers.map((d) => parseFloat(d.litros)),
          },
          {
            name: "KM Rodados",
            type: "bar",
            stack: "total",
            label: {
              show: true,
              position: "inside",
              color: "#ffffff",
            },
            emphasis: {
              focus: "series",
            },
            data: drivers.map((d) => parseInt(d.quilometrosrodados)),
          },
          {
            name: "KM/L",
            type: "bar",
            stack: "total",
            label: {
              show: true,
              position: "inside",
              color: "#ffffff",
            },
            emphasis: {
              focus: "series",
            },
            data: drivers.map((d) => parseFloat(d.quilometrosporlitro)),
          },
        ],
      };

      setOptions(option);
    } catch (error) {
      console.error("Error updating chart options:", error);
    }
  };

  useEffect(() => {
    if (data) {
      const maxValue = calculateMaxKmL(data);
      setMaxKmL(maxValue);
      updateOptions();
    }
  }, [data, filterValues]);

  async function getFuelPerKm() {
    setLoading(true);

    try {
      // For demo purposes, use the provided data instead of making an API call
      // This simulates the API response with your data
      const mockData = {
        total_abastecido_valor: "128.106,60",
        total_de_abastecimentos: 1263,
        total_litros: 30215.61,
        total_quilometrosrodados: 2573395,
        media_preco_gasolina: "5,83",
        media_preco_etanol: "4,20",
        media_preco_diesel: "7,19",
        abastecimentos_por_motorista: [
          // Your data is here...
        ],
      };

      // In a real scenario, you would make an API call:
      /*
      const url = formattUrlParams({
        centerCost,
        branch,
        startDate,
        endDate,
        baseUrl: "https://radar.tlpservicos.com.br/api/tracking/get-fuel-by-driver",
      });
      const { data } = await axios.get(url);
      */

      // For demo purposes, simulate API response
      setTimeout(() => {
        // Read the data from window object - this would be populated in your real app
        const responseData = window.apiData || mockData;
        setData(responseData);
        setLoading(false);
      }, 500);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  }

  // Store the passed data in window for the mock API
  useEffect(() => {
    // This would be your API data
    if (typeof window !== "undefined") {
      window.apiData = JSON.parse(
        '{"total_abastecido_valor":"128.106,60","total_de_abastecimentos":1263,"total_litros":30215.61,"total_quilometrosrodados":2573395,"media_preco_gasolina":"5,83","media_preco_etanol":"4,20","media_preco_diesel":"7,19","abastecimentos_por_motorista":[{"id":2400000155,"nome_motorista":"ISAAC FRANCISCO DA SILVA","abastecimentos_realizados":19,"valor":"1.657,16","litros":403.58,"quilometrosrodados":2141,"quilometrosporlitro":"6,44","veiculo":"STRADA ENDURANC","categoria_veiculo":"PICKUP LEVE","combustivel:":"ETANOL","filial":"24"},{"id":2400000346,"nome_motorista":"CRISTIANO DO ESPIRITO SANTO","abastecimentos_realizados":15,"valor":"1.604,04","litros":377.15000000000003,"quilometrosrodados":1724,"quilometrosporlitro":"5,53","veiculo":"STRADA ENDURANC","categoria_veiculo":"PICKUP LEVE","combustivel:":"ETANOL","filial":"24"}]}'
      );
    }
  }, []);

  const handleGenerateCharts = () => {
    getFuelPerKm();
  };

  const handleSliderChange = (value) => {
    setKmFilter(value[0]);
  };

  const handleSupplyChange = (value) => {
    setSupplyFilter(value);
  };

  const applyFilters = () => {
    setFilterValues({
      kmL: kmFilter,
      supplies: supplyFilter,
    });
  };

  return (
    <>
      <div className="flex gap-2 mb-4">
        <SelectCenterCost />
        <SelectBranch />
        <DatePickerStart />
        <DatePickerEnd />
        <Button className="bg-gray-200" onClick={handleGenerateCharts}>
          Gerar Gráficos
        </Button>
      </div>

      {data && (
        <>
          <div className="flex gap-4 mb-4">
            <Card className="w-1/6">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Total Abastecido</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(data.total_abastecido_valor)}
                </div>
                <p className="text-xs text-muted-foreground">
                  {formatNumber(data.total_de_abastecimentos)} abastecimentos
                </p>
              </CardContent>
            </Card>
            <Card className="w-1/6">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Total Litros</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatNumber(data.total_litros)}
                </div>
                <p className="text-xs text-muted-foreground">Litros consumidos</p>
              </CardContent>
            </Card>
            <Card className="w-1/6">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  Quilômetros Rodados
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatNumber(data.total_quilometrosrodados)}
                </div>
                <p className="text-xs text-muted-foreground">Total KM</p>
              </CardContent>
            </Card>
            <Card className="w-1/6">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Preço Gasolina</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(data.media_preco_gasolina)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Média preço gasolina no período
                </p>
              </CardContent>
            </Card>
            <Card className="w-1/6">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Preço Etanol</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(data.media_preco_etanol)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Média preço etanol no período
                </p>
              </CardContent>
            </Card>
            <Card className="w-1/6">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Preço Diesel</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(data.media_preco_diesel)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Média preço diesel no período
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="flex mb-4 p-4 bg-gray-600 rounded-sm space-x-8">
            <Button
              onClick={applyFilters}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              Aplicar Filtros
            </Button>
            <div className="w-[25%] flex flex-col gap-4 justify-center">
              <p className="text-white mb-2">KM/L menor que {kmFilter}</p>
              <Slider
                defaultValue={[10.0]}
                max={maxKmL}
                step={0.1}
                value={[kmFilter]}
                onValueChange={handleSliderChange}
                className="w-[100%]"
              />
            </div>

            <div className="flex flex-col">
              <p className="text-white my-1">Quantidade mínima de abastecimentos</p>
              <Input
                type="number"
                min={0}
                value={supplyFilter}
                onChange={(e) => handleSupplyChange(Number(e.target.value))}
                className="w-[100px] bg-white text-black"
              />
            </div>
          </div>
        </>
      )}

      {loading ? (
        <div className="h-[450px] bg-gray-500 flex justify-center items-center rounded-sm">
          <Icons.spinner className="h-12 w-12 animate-spin text-orange-300" />
        </div>
      ) : data ? (
        <div className="bg-gray-500 shadow-md rounded-sm">
          {filteredDrivers.length > 0 ? (
            <ReactECharts
              option={options}
              style={{
                height: `${Math.max(450, filteredDrivers.length * 40)}px`,
                width: "100%",
              }}
              opts={{ renderer: "canvas" }}
            />
          ) : (
            <div className="h-[450px] flex justify-center items-center">
              <p className="bg-slate-100 p-2 text-gray-500 rounded-md font-extrabold">
                SEM DADOS PARA OS FILTROS SELECIONADOS
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="h-[450px] bg-gray-500 flex justify-center items-center rounded-sm">
          <p className="bg-slate-100 p-2 text-gray-500 rounded-md font-extrabold">
            SEM DADOS
          </p>
        </div>
      )}
    </>
  );
}
